/**
 * ChauparGameModeSelector — Mode selection component
 *
 * Modes:
 * - Single Player (Easy/Medium/Hard, human + AI teammate vs 2 AI opponents)
 * - Local Multiplayer (4 players, team display)
 * - Online Multiplayer (Create/Join Room)
 */

import React, { useState } from 'react';
import {
  ChauparGameSettings,
  ChauparGameModeSelectorProps,
  ChauparDifficulty,
} from '../types';
import './ChauparGameModeSelector.css';

export const ChauparGameModeSelector: React.FC<ChauparGameModeSelectorProps> = ({
  onStartGame,
  onJoinPrivateGame,
  guestGamesRemaining,
  isAuthenticated,
  isOnline,
}) => {
  const [selectedMode, setSelectedMode] = useState<string | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<ChauparDifficulty>('medium');
  const [roomCode, setRoomCode] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  const handleStartSinglePlayer = () => {
    onStartGame({
      mode: 'singleplayer',
      aiDifficulty: selectedDifficulty,
    });
  };

  const handleStartLocalMultiplayer = () => {
    onStartGame({
      mode: 'local-multiplayer',
    });
  };

  const handleCreateRoom = () => {
    onStartGame({
      mode: 'multiplayer-realtime',
      isPrivate: true,
    });
  };

  const handleJoinRoom = async () => {
    if (!roomCode.trim() || !onJoinPrivateGame) return;

    setIsJoining(true);
    setJoinError(null);

    try {
      await onJoinPrivateGame(roomCode.trim().toUpperCase());
    } catch (error) {
      setJoinError('Failed to join room. Check the code and try again.');
    } finally {
      setIsJoining(false);
    }
  };

  return (
    <div className="chaupar-mode-selector" role="region" aria-label="Game mode selection">
      <div className="mode-header">
        <h2 className="mode-title">🏰 Chaupar</h2>
        <p className="mode-subtitle">The Royal Game of India</p>
        {!isAuthenticated && guestGamesRemaining !== undefined && (
          <p className="guest-games-info">
            {guestGamesRemaining > 0
              ? `${guestGamesRemaining} guest games remaining`
              : 'Create an account to continue playing'}
          </p>
        )}
      </div>

      {!selectedMode && (
        <div className="mode-options">
          <button
            className="mode-card"
            onClick={() => setSelectedMode('single')}
            aria-label="Single Player mode"
          >
            <span className="mode-icon">🤖</span>
            <span className="mode-name">Single Player</span>
            <span className="mode-desc">You + AI teammate vs 2 AI opponents</span>
          </button>

          <button
            className="mode-card"
            onClick={() => setSelectedMode('local')}
            aria-label="Local Multiplayer mode"
          >
            <span className="mode-icon">👥</span>
            <span className="mode-name">Local Multiplayer</span>
            <span className="mode-desc">4 players on one device (2 teams)</span>
          </button>

          <button
            className="mode-card"
            onClick={() => setSelectedMode('online')}
            disabled={!isOnline}
            aria-label="Online Multiplayer mode"
          >
            <span className="mode-icon">🌐</span>
            <span className="mode-name">Online Multiplayer</span>
            <span className="mode-desc">Play with friends online</span>
            {!isOnline && <span className="mode-offline">Offline</span>}
          </button>
        </div>
      )}

      {/* Single Player — Difficulty Selection */}
      {selectedMode === 'single' && (
        <div className="difficulty-selection">
          <h3>Select Difficulty</h3>
          <p className="team-info">You (Red) + AI Partner (Green) vs AI Blue + AI Yellow</p>

          <div className="difficulty-options">
            {(['easy', 'medium', 'hard'] as ChauparDifficulty[]).map((diff) => (
              <button
                key={diff}
                className={`difficulty-btn ${selectedDifficulty === diff ? 'selected' : ''}`}
                onClick={() => setSelectedDifficulty(diff)}
                aria-pressed={selectedDifficulty === diff}
              >
                <span className="diff-icon">
                  {diff === 'easy' ? '🌱' : diff === 'medium' ? '⚔️' : '👑'}
                </span>
                <span className="diff-name">{diff.charAt(0).toUpperCase() + diff.slice(1)}</span>
                <span className="diff-desc">
                  {diff === 'easy' ? 'Random moves' : diff === 'medium' ? 'Basic strategy' : 'Full team strategy'}
                </span>
              </button>
            ))}
          </div>

          <button className="start-btn" onClick={handleStartSinglePlayer}>
            Start Game
          </button>
          <button className="back-btn" onClick={() => setSelectedMode(null)}>
            ← Back
          </button>
        </div>
      )}

      {/* Local Multiplayer */}
      {selectedMode === 'local' && (
        <div className="local-setup">
          <h3>Local Multiplayer</h3>
          <div className="team-display">
            <div className="team-card team-a">
              <h4>Team A</h4>
              <div className="team-players">
                <span className="player-badge red">🔴 Red (Player 1)</span>
                <span className="player-badge green">🟢 Green (Player 3)</span>
              </div>
            </div>
            <span className="vs-text">VS</span>
            <div className="team-card team-b">
              <h4>Team B</h4>
              <div className="team-players">
                <span className="player-badge blue">🔵 Blue (Player 2)</span>
                <span className="player-badge yellow">🟡 Yellow (Player 4)</span>
              </div>
            </div>
          </div>

          <button className="start-btn" onClick={handleStartLocalMultiplayer}>
            Start Game
          </button>
          <button className="back-btn" onClick={() => setSelectedMode(null)}>
            ← Back
          </button>
        </div>
      )}

      {/* Online Multiplayer */}
      {selectedMode === 'online' && (
        <div className="online-setup">
          <h3>Online Multiplayer</h3>

          <button className="create-room-btn" onClick={handleCreateRoom}>
            🏠 Create Room
          </button>

          <div className="join-room-section">
            <p className="join-label">Or join an existing room:</p>
            <div className="join-input-row">
              <input
                type="text"
                className="room-code-input"
                placeholder="Enter room code"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                maxLength={6}
                aria-label="Room code"
              />
              <button
                className="join-btn"
                onClick={handleJoinRoom}
                disabled={!roomCode.trim() || isJoining}
              >
                {isJoining ? 'Joining...' : 'Join'}
              </button>
            </div>
            {joinError && <p className="join-error">{joinError}</p>}
          </div>

          <button className="back-btn" onClick={() => setSelectedMode(null)}>
            ← Back
          </button>
        </div>
      )}
    </div>
  );
};

export default ChauparGameModeSelector;
